/**
 * Admin & Settings → Users & Access. Tabbed workspace: Users, Roles &
 * Permissions, Invitations, Sessions & Security. Talks to the enforced
 * /api/access/* + /api/auth/* backend (cookies). Permission-aware: actions are
 * hidden without the permission AND enforced server-side. Professional SaaS
 * tables + drawers, AVOS palette.
 */
import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from "react";
import { useMe } from "../useMe.js";

type Tab = "users" | "roles" | "invitations" | "sessions";
const TABS: readonly { key: Tab; label: string; perm?: string }[] = [
  { key: "users", label: "Users", perm: "users.view" },
  { key: "roles", label: "Roles & Permissions", perm: "roles.view" },
  { key: "invitations", label: "Invitations", perm: "users.view" },
  { key: "sessions", label: "Sessions & Security" },
];

const ROLE_OPTIONS = [
  ["Owner", "Owner"], ["PropertyManager", "Property Manager"], ["Reservations", "Reservations"],
  ["RevenueManager", "Revenue Manager"], ["Finance", "Finance"], ["Operations", "Operations"],
  ["Marketing", "Marketing"], ["Staff", "Staff"], ["ReadOnly", "Read Only"],
] as const;
function roleLabel(role: string | null): string {
  if (!role) return "—";
  return ROLE_OPTIONS.find(([v]) => v === role)?.[1] ?? role;
}

/**
 * Safe default property-access mode for a role. Owners get org-wide access
 * ("all"); every other role defaults to the least-privilege "selected" (zero
 * properties until an admin grants some). Used to seed Add Member and to
 * re-derive the mode on a role change when the admin has not manually chosen.
 */
function defaultAccessForRole(role: string): "all" | "selected" {
  return role === "Owner" ? "all" : "selected";
}

/**
 * Human-readable one-line summary of a granted property access selection, for
 * the create-result card. "All properties", or "Selected — NameA, NameB" for up
 * to three resolved names, collapsing to "Selected — N properties" beyond that
 * (or when names are unavailable). `names` should already be resolved from the
 * tenant property map; falls back to a count when empty.
 */
function accessSummaryText(mode: "all" | "selected", names: string[], count: number): string {
  if (mode === "all") return "All properties";
  if (count === 0) return "Selected — 0 properties";
  if (names.length > 0 && names.length <= 3 && names.length === count) return `Selected — ${names.join(", ")}`;
  return `Selected — ${count} properties`;
}

async function api(path: string, body?: unknown): Promise<{ status: number; json: any }> {
  const res = await fetch(path, { method: "POST", headers: { "content-type": "application/json" }, credentials: "include", body: JSON.stringify(body ?? {}) });
  let json: any = {}; try { json = await res.json(); } catch {}
  return { status: res.status, json };
}
function errText(j: any): string { return j?.error?.error?.message ?? "Something went wrong."; }

export default function UsersAccessScreen(): ReactElement {
  const me = useMe();
  const [tab, setTab] = useState<Tab>("users");
  const visibleTabs = TABS.filter((t) => !t.perm || me.can(t.perm) || !me.user);
  return (
    <section className="ua-screen">
      <header className="ua-head">
        <div>
          <h1 className="console-main__title">Users &amp; Access</h1>
          <p className="ua-sub">Manage your team, their roles, property access, and active sessions.</p>
        </div>
      </header>
      <nav className="ua-tabs" aria-label="Users and access">
        {visibleTabs.map((t) => (
          <button key={t.key} type="button" className={t.key === tab ? "ua-tab ua-tab--active" : "ua-tab"} aria-current={t.key === tab ? "page" : undefined} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </nav>
      <div className="ua-panel-wrap">
        {tab === "users" && <UsersTab />}
        {tab === "roles" && <RolesTab />}
        {tab === "invitations" && <InvitationsTab />}
        {tab === "sessions" && <SessionsTab />}
      </div>
    </section>
  );
}

function StatusBadge({ status }: { readonly status: string }): ReactElement {
  const cls = status === "active" ? "ua-badge ua-badge--ok"
    : status === "invited" ? "ua-badge ua-badge--info"
    : status === "suspended" ? "ua-badge ua-badge--warn" : "ua-badge";
  return <span className={cls}>{status}</span>;
}

/**
 * Copy a string to the clipboard. Prefers the async Clipboard API; falls back
 * to a hidden textarea + execCommand('copy') when it is unavailable (older
 * browsers / insecure contexts). Returns whether the copy appears to have
 * succeeded. The plaintext is never persisted anywhere.
 */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through to legacy path */ }
  try {
    if (typeof document === "undefined") return false;
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "absolute";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * The "team member created / password reset" result card. Shows the one-time
 * temporary password masked by default with Show/Hide + Copy controls, plus the
 * truthful no-email-delivery guidance. The plaintext lives ONLY in this
 * component's props for the lifetime of the card — it is never stored, logged,
 * or re-fetchable. Closing the card loses it permanently.
 */
function TempPasswordCard({
  title,
  email,
  temporaryPassword,
  accessSummary,
  onDone,
}: {
  readonly title: string;
  readonly email: string;
  readonly temporaryPassword: string;
  readonly accessSummary?: string;
  readonly onDone: () => void;
}): ReactElement {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  async function copy(): Promise<void> {
    const ok = await copyToClipboard(temporaryPassword);
    setCopied(ok);
    if (ok) setTimeout(() => setCopied(false), 2000);
  }
  return (
    <div data-testid="temp-password-card">
      <h2 className="ua-h2">{title}</h2>
      <p className="ua-muted">Email: <strong>{email}</strong></p>
      <label className="ua-field">
        <span>Temporary password</span>
        <div className="ua-row" style={{ alignItems: "center" }}>
          <input
            type={show ? "text" : "password"}
            value={temporaryPassword}
            readOnly
            data-testid="temp-password-input"
            onFocus={(e) => e.currentTarget.select()}
            style={{ fontFamily: "var(--font-mono, monospace)", flex: 1 }}
          />
          <button type="button" className="console-btn--ghost" onClick={() => setShow((v) => !v)}>
            {show ? "Hide" : "Show"}
          </button>
          <button type="button" className="console-btn--ghost" onClick={copy}>
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </label>
      <p className="ua-note" role="note">
        Email delivery is not configured yet. Share this temporary password securely with the
        team member. This password is shown only once — they will be required to create a new
        password when they first sign in.
      </p>
      {accessSummary && <p className="ua-muted" data-testid="temp-password-access">Property access: {accessSummary}</p>}
      <div className="ua-drawer__actions">
        <button type="button" className="console-btn" onClick={onDone}>Done</button>
      </div>
    </div>
  );
}

function UsersTab(): ReactElement {
  const me = useMe();
  const [users, setUsers] = useState<any[] | null>(null);
  const [propMap, setPropMap] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState<any | null>(null);
  const load = useCallback(async () => { const r = await api("/api/access/users/list"); setUsers(r.json.users ?? []); }, []);
  useEffect(() => { void load(); }, [load]);
  // Load the tenant property id→name map once so the table (and the summaries
  // passed down to the drawers) can render real names instead of raw counts.
  useEffect(() => {
    void api("/api/access/properties/list").then((r) => {
      const m: Record<string, string> = {};
      for (const p of (r.json.properties ?? [])) m[p.id] = p.name;
      setPropMap(m);
    });
  }, []);

  /** Property-access cell text, resolving a single selection to its name. */
  function accessCell(u: any): string {
    if (u.propertyAccess === "all") return "All properties";
    const ids: string[] = u.propertyIds ?? [];
    if (ids.length === 0) return "0 properties";
    if (ids.length === 1) return propMap[ids[0]!] ?? "1 property";
    return `${ids.length} properties`;
  }

  async function setStatus(u: any, status: string): Promise<void> {
    if (status !== "active" && !confirm(`Really ${status} ${u.email}?`)) return;
    const r = await api("/api/access/users/set-status", { userId: u.id, status });
    if (r.status !== 200) setMsg(errText(r.json)); else { setMsg(null); void load(); }
  }

  return (
    <div>
      {msg && <p className="ua-error" role="alert">{msg}</p>}
      <div className="ua-toolbar">
        <div className="ua-kpis">
          <span className="ua-kpi">{(users ?? []).filter((u) => u.status === "active").length} active</span>
          <span className="ua-kpi">{(users ?? []).filter((u) => u.status === "invited").length} invited</span>
          <span className="ua-kpi">{(users ?? []).filter((u) => u.status === "suspended").length} suspended</span>
        </div>
        <div className="ua-toolbar__actions">
          {me.can("users.invite") && <button type="button" className="console-btn--ghost" onClick={() => setInviteOpen(true)}>+ Invite user</button>}
          {me.can("users.invite") && <button type="button" className="console-btn" onClick={() => setAddOpen(true)}>+ Add member</button>}
        </div>
      </div>
      <div className="ua-tablewrap">
        <table className="ua-table">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Property access</th><th>Status</th><th>Last login</th><th></th></tr></thead>
          <tbody>
            {users === null ? <tr><td colSpan={7} className="ua-muted">Loading…</td></tr>
              : users.length === 0 ? <tr><td colSpan={7} className="ua-muted">No users.</td></tr>
              : users.map((u) => (
                <tr key={u.id}>
                  <td>{[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}</td>
                  <td>{u.email}</td>
                  <td>{roleLabel(u.role)}</td>
                  <td>{accessCell(u)}</td>
                  <td><StatusBadge status={u.status} /></td>
                  <td>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : "—"}</td>
                  <td className="ua-actions">
                    {me.can("users.edit") && <button type="button" className="ua-link" onClick={() => setEditUser(u)}>Manage</button>}
                    {me.can("users.suspend") && u.status === "active" && <button type="button" className="ua-link-danger" onClick={() => setStatus(u, "suspended")}>Suspend</button>}
                    {me.can("users.suspend") && u.status === "suspended" && <button type="button" className="ua-link" onClick={() => setStatus(u, "active")}>Reactivate</button>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {inviteOpen && <InviteDrawer onClose={() => setInviteOpen(false)} onDone={() => { setInviteOpen(false); void load(); }} />}
      {addOpen && <AddMemberDrawer propMap={propMap} onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); void load(); }} />}
      {editUser && <UserDrawer user={editUser} propMap={propMap} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); void load(); }} />}
    </div>
  );
}

function InviteDrawer({ onClose, onDone }: { readonly onClose: () => void; readonly onDone: () => void }): ReactElement {
  const [f, setF] = useState<any>({ firstName: "", lastName: "", email: "", role: "Reservations", propertyAccess: "all", propertyIds: [] as string[], jobTitle: "", department: "", message: "" });
  const [props, setProps] = useState<any[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { void api("/api/access/properties/list").then((r) => setProps(r.json.properties ?? [])); }, []);
  const set = (k: string) => (e: any) => setF((p: any) => ({ ...p, [k]: e.target.value }));
  function toggleProp(id: string): void { setF((p: any) => ({ ...p, propertyIds: p.propertyIds.includes(id) ? p.propertyIds.filter((x: string) => x !== id) : [...p.propertyIds, id] })); }
  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault(); setBusy(true); setErr(null);
    const r = await api("/api/access/invitations/create", f); setBusy(false);
    if (r.status === 200 && r.json.ok) setLink(`/invite/accept?token=${r.json.inviteToken}`); else setErr(errText(r.json));
  }
  return (
    <div className="ua-drawer" role="dialog" aria-modal="true">
      <div className="ua-drawer__backdrop" onClick={onClose} />
      <div className="ua-drawer__panel">
        {link ? (
          <div><h2 className="ua-h2">Invitation sent</h2><p className="ua-muted">Share this link (also emailed):</p><code className="ua-code">{link}</code><div className="ua-drawer__actions"><button type="button" className="console-btn" onClick={onDone}>Done</button></div></div>
        ) : (
          <form onSubmit={submit} noValidate>
            <h2 className="ua-h2">Invite user</h2>
            {err && <p className="ua-error" role="alert">{err}</p>}
            <div className="ua-row"><label className="ua-field"><span>First name *</span><input value={f.firstName} onChange={set("firstName")} /></label><label className="ua-field"><span>Last name</span><input value={f.lastName} onChange={set("lastName")} /></label></div>
            <label className="ua-field"><span>Email *</span><input type="email" value={f.email} onChange={set("email")} /></label>
            <label className="ua-field"><span>Role *</span><select value={f.role} onChange={set("role")}>{ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
            <div className="ua-row"><label className="ua-field"><span>Job title</span><input value={f.jobTitle} onChange={set("jobTitle")} /></label><label className="ua-field"><span>Department</span><input value={f.department} onChange={set("department")} /></label></div>
            <fieldset className="ua-fieldset">
              <legend>Property access *</legend>
              <label className="ua-radio"><input type="radio" name="pa" checked={f.propertyAccess === "all"} onChange={() => setF((p: any) => ({ ...p, propertyAccess: "all" }))} /> All properties</label>
              <label className="ua-radio"><input type="radio" name="pa" checked={f.propertyAccess === "selected"} onChange={() => setF((p: any) => ({ ...p, propertyAccess: "selected" }))} /> Selected properties</label>
              {f.propertyAccess === "selected" && (
                <div className="ua-proplist">{props.map((p) => <label key={p.id} className="ua-check"><input type="checkbox" checked={f.propertyIds.includes(p.id)} onChange={() => toggleProp(p.id)} /> {p.name}</label>)}</div>
              )}
            </fieldset>
            <div className="ua-drawer__actions"><button type="button" className="console-btn--ghost" onClick={onClose}>Cancel</button><button type="submit" className="console-btn" disabled={busy}>{busy ? "Inviting…" : "Send invitation"}</button></div>
          </form>
        )}
      </div>
    </div>
  );
}

/**
 * Add member — creates an ACTIVE user directly (no email invitation) via
 * /api/access/users/create. The backend returns a one-time temporary password
 * which we surface once in a {@link TempPasswordCard}. The form mirrors the
 * invite form (name / email / role / property access) but the submit target,
 * copy, and success state are different: this is "add member", not "invite".
 */
function AddMemberDrawer({ propMap, onClose, onDone }: { readonly propMap: Record<string, string>; readonly onClose: () => void; readonly onDone: () => void }): ReactElement {
  // Role default stays non-Owner, but property access is seeded from the role:
  // non-Owner → least-privilege "selected"; Owner → "all".
  const initialRole = "Reservations";
  const [f, setF] = useState<any>({ firstName: "", lastName: "", email: "", role: initialRole, propertyAccess: defaultAccessForRole(initialRole), propertyIds: [] as string[] });
  // Whether the admin has manually picked an access mode. While false, changing
  // the role re-derives the mode from defaultAccessForRole.
  const [touchedAccess, setTouchedAccess] = useState(false);
  const [props, setProps] = useState<any[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [created, setCreated] = useState<{ email: string; temporaryPassword: string; accessSummary: string } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { void api("/api/access/properties/list").then((r) => setProps(r.json.properties ?? [])); }, []);
  const set = (k: string) => (e: any) => setF((p: any) => ({ ...p, [k]: e.target.value }));
  function changeRole(e: any): void {
    const role = e.target.value;
    setF((p: any) => ({ ...p, role, propertyAccess: touchedAccess ? p.propertyAccess : defaultAccessForRole(role) }));
  }
  function pickMode(mode: "all" | "selected"): void { setTouchedAccess(true); setF((p: any) => ({ ...p, propertyAccess: mode })); }
  function toggleProp(id: string): void { setF((p: any) => ({ ...p, propertyIds: p.propertyIds.includes(id) ? p.propertyIds.filter((x: string) => x !== id) : [...p.propertyIds, id] })); }
  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault(); setBusy(true); setErr(null);
    const r = await api("/api/access/users/create", {
      email: f.email, firstName: f.firstName || undefined, lastName: f.lastName || undefined,
      role: f.role, propertyAccess: f.propertyAccess, propertyIds: f.propertyIds,
    });
    setBusy(false);
    if (r.status === 200 && r.json.ok && r.json.temporaryPassword) {
      const names = (f.propertyIds as string[]).map((id) => propMap[id]).filter(Boolean) as string[];
      setCreated({
        email: r.json.user?.email ?? f.email,
        temporaryPassword: r.json.temporaryPassword,
        accessSummary: accessSummaryText(f.propertyAccess, names, f.propertyIds.length),
      });
    } else if (r.status === 409) {
      setErr("A user with this email already exists.");
    } else {
      setErr(errText(r.json));
    }
  }
  return (
    <div className="ua-drawer" role="dialog" aria-modal="true">
      <div className="ua-drawer__backdrop" onClick={onClose} />
      <div className="ua-drawer__panel">
        {created ? (
          <TempPasswordCard title="Team member created" email={created.email} temporaryPassword={created.temporaryPassword} accessSummary={created.accessSummary} onDone={onDone} />
        ) : (
          <form onSubmit={submit} noValidate>
            <h2 className="ua-h2">Add member</h2>
            {err && <p className="ua-error" role="alert">{err}</p>}
            <div className="ua-row"><label className="ua-field"><span>First name</span><input value={f.firstName} onChange={set("firstName")} /></label><label className="ua-field"><span>Last name</span><input value={f.lastName} onChange={set("lastName")} /></label></div>
            <label className="ua-field"><span>Email *</span><input type="email" value={f.email} onChange={set("email")} /></label>
            <label className="ua-field"><span>Role *</span><select value={f.role} onChange={changeRole}>{ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
            <fieldset className="ua-fieldset">
              <legend>Property access *</legend>
              <label className="ua-radio"><input type="radio" name="apa" checked={f.propertyAccess === "all"} onChange={() => pickMode("all")} /> All properties</label>
              <label className="ua-radio"><input type="radio" name="apa" checked={f.propertyAccess === "selected"} onChange={() => pickMode("selected")} /> Selected properties</label>
              {f.propertyAccess === "selected" && <PropChecklist props={props} selected={f.propertyIds} onToggle={toggleProp} />}
            </fieldset>
            <div className="ua-drawer__actions"><button type="button" className="console-btn--ghost" onClick={onClose}>Cancel</button><button type="submit" className="console-btn" disabled={busy}>{busy ? "Creating…" : "Add member"}</button></div>
          </form>
        )}
      </div>
    </div>
  );
}

/**
 * Shared property checklist with explicit loading / empty-active-list states so
 * a "Selected" access mode never renders a blank box. `props === null` means
 * the tenant property list is still loading; an empty array means there are no
 * active properties in this account.
 */
function PropChecklist({ props, selected, onToggle }: { readonly props: any[] | null; readonly selected: string[]; readonly onToggle: (id: string) => void }): ReactElement {
  if (props === null) return <p className="ua-muted" data-testid="props-loading">Loading properties…</p>;
  if (props.length === 0) return <p className="ua-muted" data-testid="props-empty">No active properties in this account yet.</p>;
  return (
    <div className="ua-proplist">
      {props.map((p) => <label key={p.id} className="ua-check"><input type="checkbox" checked={selected.includes(p.id)} onChange={() => onToggle(p.id)} /> {p.name}</label>)}
    </div>
  );
}

function UserDrawer({ user, propMap, onClose, onSaved }: { readonly user: any; readonly propMap: Record<string, string>; readonly onClose: () => void; readonly onSaved: () => void }): ReactElement {
  const me = useMe();
  const [role, setRole] = useState(user.role ?? "Reservations");
  const [mode, setMode] = useState<"all" | "selected">(user.propertyAccess ?? "all");
  const [ids, setIds] = useState<string[]>(user.propertyIds ?? []);
  // Seeded from the user's current mode so merely opening the drawer never flips
  // anything; only an actual role change (while false) re-derives the default.
  const [touchedAccess, setTouchedAccess] = useState(false);
  const [props, setProps] = useState<any[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Active-status control: current status + deactivate/reactivate action.
  const [status, setStatus] = useState<string>(user.status ?? "active");
  const [statusBusy, setStatusBusy] = useState(false);
  // Reset-password sub-flow: idle → confirming → (POST) → result card.
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetErr, setResetErr] = useState<string | null>(null);
  const [resetPw, setResetPw] = useState<string | null>(null);
  useEffect(() => { void api("/api/access/properties/list").then((r) => setProps(r.json.properties ?? [])); }, []);
  function toggle(id: string): void { setIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]); }
  function pickMode(next: "all" | "selected"): void { setTouchedAccess(true); setMode(next); }
  function changeRole(e: any): void {
    const next = e.target.value;
    setRole(next);
    if (!touchedAccess) setMode(defaultAccessForRole(next));
  }
  async function doReset(): Promise<void> {
    setResetBusy(true); setResetErr(null);
    const r = await api("/api/access/users/reset-password", { userId: user.id });
    setResetBusy(false); setConfirmReset(false);
    if (r.status === 200 && r.json.ok && r.json.temporaryPassword) {
      setResetPw(r.json.temporaryPassword);
    } else if (r.status === 404) {
      setResetErr("User not found.");
    } else {
      setResetErr(errText(r.json));
    }
  }
  async function toggleStatus(): Promise<void> {
    const next = status === "active" ? "suspended" : "active";
    if (next === "suspended" && !confirm(`Deactivate ${user.email}? They will be signed out and unable to sign in until reactivated.`)) return;
    setStatusBusy(true); setErr(null);
    const r = await api("/api/access/users/set-status", { userId: user.id, status: next });
    setStatusBusy(false);
    if (r.status !== 200) { setErr(errText(r.json)); return; }
    setStatus(next);
    onSaved();
  }
  async function save(): Promise<void> {
    setBusy(true); setErr(null);
    if (me.can("roles.manage") && role !== user.role) {
      const r = await api("/api/access/users/set-role", { userId: user.id, role });
      if (r.status !== 200) { setErr(errText(r.json)); setBusy(false); return; }
    }
    const r2 = await api("/api/access/users/set-property-access", { userId: user.id, mode, propertyIds: ids });
    setBusy(false);
    if (r2.status !== 200) setErr(errText(r2.json)); else onSaved();
  }
  return (
    <div className="ua-drawer" role="dialog" aria-modal="true">
      <div className="ua-drawer__backdrop" onClick={onClose} />
      <div className="ua-drawer__panel">
        {resetPw ? (
          <TempPasswordCard
            title="Temporary password generated"
            email={user.email}
            temporaryPassword={resetPw}
            accessSummary={accessSummaryText(mode, ids.map((id) => propMap[id]).filter(Boolean) as string[], ids.length)}
            onDone={onSaved}
          />
        ) : (
          <>
            <h2 className="ua-h2">{user.email}</h2>
            {err && <p className="ua-error" role="alert">{err}</p>}
            <label className="ua-field"><span>Role</span>
              <select value={role} onChange={changeRole} disabled={!me.can("roles.manage")}>{ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            </label>
            <fieldset className="ua-fieldset">
              <legend>Property access</legend>
              <label className="ua-radio"><input type="radio" checked={mode === "all"} onChange={() => pickMode("all")} /> All properties</label>
              <label className="ua-radio"><input type="radio" checked={mode === "selected"} onChange={() => pickMode("selected")} /> Selected properties</label>
              {mode === "selected" && <PropChecklist props={props} selected={ids} onToggle={toggle} />}
            </fieldset>
            <div className="ua-drawer__actions"><button type="button" className="console-btn--ghost" onClick={onClose}>Cancel</button><button type="button" className="console-btn" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save changes"}</button></div>

            {me.can("users.suspend") && (
              <fieldset className="ua-fieldset" style={{ marginTop: "1.25rem" }}>
                <legend>Active status</legend>
                <p className="ua-muted">Current status: <StatusBadge status={status} /></p>
                {status === "active" ? (
                  <button type="button" className="console-btn--ghost" data-testid="deactivate-user" onClick={toggleStatus} disabled={statusBusy}>{statusBusy ? "Working…" : "Deactivate user"}</button>
                ) : (
                  <button type="button" className="console-btn--ghost" data-testid="reactivate-user" onClick={toggleStatus} disabled={statusBusy}>{statusBusy ? "Working…" : "Reactivate user"}</button>
                )}
              </fieldset>
            )}

            <fieldset className="ua-fieldset" style={{ marginTop: "1.25rem" }}>
              <legend>Reset password</legend>
              {resetErr && <p className="ua-error" role="alert">{resetErr}</p>}
              {confirmReset ? (
                <div>
                  <p className="ua-muted">Are you sure? This signs the user out of all devices and forces a new password on next sign-in.</p>
                  <div className="ua-drawer__actions">
                    <button type="button" className="console-btn--ghost" onClick={() => setConfirmReset(false)} disabled={resetBusy}>Cancel</button>
                    <button type="button" className="console-btn" data-testid="confirm-reset-password" onClick={doReset} disabled={resetBusy}>{resetBusy ? "Generating…" : "Generate new temporary password"}</button>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="ua-muted">Generate a new temporary password for this user.</p>
                  <button type="button" className="console-btn--ghost" onClick={() => { setResetErr(null); setConfirmReset(true); }}>Generate a new temporary password</button>
                </div>
              )}
            </fieldset>
          </>
        )}
      </div>
    </div>
  );
}

function RolesTab(): ReactElement {
  const [roles, setRoles] = useState<any[] | null>(null);
  useEffect(() => { void api("/api/access/roles/list").then((r) => setRoles(r.json.roles ?? [])); }, []);
  return (
    <div className="ua-tablewrap">
      <table className="ua-table">
        <thead><tr><th>Role</th><th>Type</th><th>Users</th><th>Permissions</th></tr></thead>
        <tbody>
          {roles === null ? <tr><td colSpan={4} className="ua-muted">Loading…</td></tr>
            : roles.map((r) => (
              <tr key={r.role}><td>{r.label}</td><td><span className="ua-badge">{r.type}</span></td><td>{r.userCount}</td><td className="ua-muted">{r.permissions.length} permissions</td></tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

function InvitationsTab(): ReactElement {
  const me = useMe();
  const [invites, setInvites] = useState<any[] | null>(null);
  const load = useCallback(async () => { const r = await api("/api/access/invitations/list"); setInvites(r.json.invitations ?? []); }, []);
  useEffect(() => { void load(); }, [load]);
  async function revoke(id: string): Promise<void> { if (!confirm("Revoke this invitation?")) return; await api("/api/access/invitations/revoke", { invitationId: id }); void load(); }
  async function resend(id: string): Promise<void> { await api("/api/access/invitations/resend", { invitationId: id }); void load(); }
  return (
    <div className="ua-tablewrap">
      <table className="ua-table">
        <thead><tr><th>Email</th><th>Role</th><th>Status</th><th>Sent</th><th>Expires</th><th></th></tr></thead>
        <tbody>
          {invites === null ? <tr><td colSpan={6} className="ua-muted">Loading…</td></tr>
            : invites.length === 0 ? <tr><td colSpan={6} className="ua-muted">No invitations.</td></tr>
            : invites.map((i) => (
              <tr key={i.id}>
                <td>{i.email}</td><td>{roleLabel(i.role)}</td><td><StatusBadge status={i.status} /></td>
                <td>{new Date(i.createdAt).toLocaleDateString()}</td><td>{new Date(i.expiresAt).toLocaleDateString()}</td>
                <td className="ua-actions">
                  {me.can("users.invite") && i.status === "pending" && <><button type="button" className="ua-link" onClick={() => resend(i.id)}>Resend</button><button type="button" className="ua-link-danger" onClick={() => revoke(i.id)}>Revoke</button></>}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

function SessionsTab(): ReactElement {
  const [sessions, setSessions] = useState<any[] | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const load = useCallback(async () => { const r = await api("/api/auth/sessions"); setSessions(r.json.sessions ?? []); }, []);
  useEffect(() => { void load(); }, [load]);
  async function revoke(id: string): Promise<void> { const r = await api("/api/auth/sessions/revoke", { sessionId: id }); if (r.json?.ok) { setMsg("Session signed out."); void load(); } }
  async function logoutAll(): Promise<void> { if (!confirm("Sign out of all other devices?")) return; await api("/api/auth/logout-all"); window.location.href = "/login"; }
  return (
    <div>
      {msg && <p className="ua-note" role="status">{msg}</p>}
      <div className="ua-tablewrap">
        <table className="ua-table">
          <thead><tr><th>Device</th><th>Last active</th><th>Signed in</th><th></th></tr></thead>
          <tbody>
            {sessions === null ? <tr><td colSpan={4} className="ua-muted">Loading…</td></tr>
              : sessions.map((s) => (
                <tr key={s.id}>
                  <td>{s.deviceLabel}{s.current && <span className="ua-badge ua-badge--ok" style={{ marginLeft: "0.5rem" }}>This device</span>}</td>
                  <td>{new Date(s.lastActivityAt).toLocaleString()}</td>
                  <td>{new Date(s.createdAt).toLocaleDateString()}</td>
                  <td className="ua-actions">{!s.current && <button type="button" className="ua-link-danger" onClick={() => revoke(s.id)}>Sign out</button>}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: "1rem" }}><button type="button" className="console-btn--ghost" onClick={logoutAll}>Sign out all other devices</button></div>
    </div>
  );
}
